import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth, db, updateUserDatabase } from "shared/utils/firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import InputControl from "./InputControl/InputControl";
import styles from "./Auth.module.css";
import {
  MDBCol
} from 'mdb-react-ui-kit';

// Helper functions for email validation
const freeEmailProviders = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'rediffmail.com'
];

const isEducationalEmail = (email) => {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;

  // Check for .edu domains
  if (domain.endsWith('.edu')) return true;

  // Check for academic domains (.ac.*)
  if (domain.includes('.ac.')) return true;

  // Check for university-specific patterns
  if (domain.includes('university') || domain.includes('college') || domain.includes('institute')) {
    return true;
  }

  return false;
};

const isFreeEmailProvider = (email) => {
  const domain = email.split('@')[1]?.toLowerCase();
  return freeEmailProviders.includes(domain);
};

const validateEmailForRole = (email, role) => {
  if (!email || !email.includes('@')) {
    return { valid: false, message: "Please enter a valid email address", isEducational: false };
  }

  const isEdu = isEducationalEmail(email);

  switch (role) {
    case 'Student':
      // Students must use recognized free email providers only
      if (!isFreeEmailProvider(email)) {
        return {
          valid: false,
          message: "Students must use a recognized free email provider (gmail, yahoo, hotmail, outlook, etc.)",
          isEducational: false
        };
      }
      return { valid: true, message: "", isEducational: false };

    case 'Professional':
      // Professionals must use corporate emails (no free providers)
      if (isFreeEmailProvider(email)) {
        return {
          valid: false,
          message: "Professionals must use a corporate/business email address (not gmail, yahoo, etc.)",
          isEducational: false
        };
      }
      return { valid: true, message: "", isEducational: false };

    case 'Organisation':
      // Organizations can use educational emails OR corporate emails (no free providers)
      // First check if it's a free provider
      if (isFreeEmailProvider(email)) {
        return {
          valid: false,
          message: "Organizations must use an educational or corporate email address (not gmail, yahoo, etc.)",
          isEducational: false
        };
      }
      // Check if it's an educational email for special messaging
      if (isEdu) {
        return {
          valid: true,
          message: "Educational email detected",
          isEducational: true
        };
      }
      // Otherwise it's a valid corporate email
      return { valid: true, message: "", isEducational: false };

    default:
      return { valid: true, message: "", isEducational: false };
  }
};

function Auth(props) {
  const isSignup = props.signup ? true : false;
  const navigate = useNavigate();

  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    role: "Student"
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [emailValidationStatus, setEmailValidationStatus] = useState({ valid: true, message: "" });
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Check if email already exists in database
  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) return false;

    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", email.toLowerCase()));
      const querySnapshot = await getDocs(q);

      return !querySnapshot.empty;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  // Real-time email validation handler with duplicate check
  const handleEmailChange = async (email) => {
    setValues((prev) => ({ ...prev, email }));

    // Only validate if signup and email is not empty
    if (isSignup && email) {
      // First do role-based validation
      const validation = validateEmailForRole(email, values.role);

      if (validation.valid && email.includes('@')) {
        // If role validation passes, check for duplicates
        setIsCheckingEmail(true);
        const emailExists = await checkEmailExists(email);
        setIsCheckingEmail(false);

        if (emailExists) {
          setEmailValidationStatus({
            valid: false,
            message: "This email is already registered. Please login instead.",
            isEducational: false,
            isDuplicate: true
          });
          return;
        }
      }

      setEmailValidationStatus(validation);
    } else {
      setEmailValidationStatus({ valid: true, message: "" });
    }
  };

  const handleLogin = async () => {
    if (!values.email || !values.password) {
      setErrorMsg("All fields required*");
      return;
    }

    setSubmitButtonDisabled(true);
    console.log("🔐 Login attempt for:", values.email);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, values.email, values.password);
      const user = userCredential.user;

      console.log("✅ Firebase Auth successful for UID:", user.uid);

      if (user) {
        const { getUserFromDatabase, isAdminEmail, updateUserDatabase } = await import("../../utils/firebase");
        let userData = await getUserFromDatabase(user.uid);

        console.log("📦 Firestore userData retrieved:", {
          exists: !!userData,
          role: userData?.role,
          isAdmin: userData?.isAdmin,
          email: userData?.email
        });

        // If no Firestore document exists, create one
        if (!userData) {
          console.warn("⚠️ No Firestore document found! Creating one...");

          const newUserData = {
            email: user.email,
            uid: user.uid,
            role: isAdminEmail(user.email) ? 'Admin' : 'Student',
            isAdmin: isAdminEmail(user.email),
            createdAt: new Date(),
            lastLogin: new Date(),
            isActive: true,
            name: user.displayName || user.email.split('@')[0],
            phone: ''  // Initialize as empty, not email
          };

          await updateUserDatabase(newUserData, user.uid);
          console.log("✅ Firestore document created for:", user.email);
          userData = newUserData;
        }

        // Security check: Block DELETED accounts
        if (userData?.deleted === true) {
          console.error("❌ Account is deleted");
          await signOut(auth);
          setSubmitButtonDisabled(false);
          setErrorMsg("This account has been deleted. Please contact support.");
          return;
        }

        // Check for DEACTIVATED accounts - ALLOW login but store status
        if (userData?.deactivated === true) {
          console.warn("⚠️ Account is deactivated/suspended");
          sessionStorage.setItem('accountDeactivated', 'true');
          sessionStorage.setItem('deactivationReason', userData.deactivationReason || 'Account suspended.');
        } else {
          sessionStorage.removeItem('accountDeactivated');
          sessionStorage.removeItem('deactivationReason');
        }

        // Determine if user is admin
        let isAdmin = userData?.role === 'Admin' || userData?.isAdmin === true;

        // Fallback: Check email whitelist if role fields are missing
        if (!isAdmin && isAdminEmail(user.email)) {
          console.warn("⚠️ Admin detected by email fallback! Auto-fixing Firestore...");
          isAdmin = true;

          // Fix the Firestore document
          await updateUserDatabase({
            ...userData,
            role: 'Admin',
            isAdmin: true,
            updatedAt: new Date()
          }, user.uid);

          console.log("✅ Admin role auto-fixed for:", user.email);
        }

        console.log("🎯 Admin detection result:", {
          roleField: userData?.role,
          isAdminField: userData?.isAdmin,
          emailFallback: isAdminEmail(user.email),
          finalResult: isAdmin
        });

        // Navigate based on role
        if (isAdmin) {
          console.log("✅ Redirecting to /admin");
          navigate("/admin");
        } else {
          console.log("✅ Redirecting to /dashboard");
          navigate("/dashboard");
        }
      }
      setSubmitButtonDisabled(false);
    } catch (err) {
      console.error("❌ Login error:", err.code, err.message);
      setSubmitButtonDisabled(false);
      setErrorMsg(err.message);
    }
  };

  const handleSignup = () => {
    if (!values.name || !values.email || !values.password || !values.role) {
      setErrorMsg("All fields required*");
      return;
    }

    // Validate email based on role
    const emailValidation = validateEmailForRole(values.email, values.role);
    if (!emailValidation.valid) {
      setErrorMsg(emailValidation.message);
      return;
    }

    if (values.password !== values.confirmPassword) {
      setErrorMsg("Passwords do not match*");
      return;
    }

    if (values.password.length < 6) {
      setErrorMsg("Password must be at least 6 characters*");
      return;
    }

    setSubmitButtonDisabled(true);
    createUserWithEmailAndPassword(auth, values.email, values.password)
      .then(async (response) => {
        const userId = response.user.uid;

        // Prepare user data with role
        const userData = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          role: values.role,
          isAdmin: values.role === 'Admin', // Set isAdmin flag for backward compatibility
          createdAt: new Date(),
          lastLogin: new Date(),
          isActive: true
        };

        await updateUserDatabase(userData, userId);
        setSubmitButtonDisabled(false);

        // Navigate based on role after signup
        if (values.role === 'Admin') {
          navigate("/admin");
        } else {
          navigate("/dashboard/account");
        }
      })
      .catch((err) => {
        setSubmitButtonDisabled(false);
        setErrorMsg(err.message);
      });
  };

  const handleSubmission = (event) => {
    event.preventDefault();

    if (isSignup) handleSignup();
    else handleLogin();
  };

  return (
    <div className={styles.container}>
      <form className={styles.form} onSubmit={handleSubmission}>
        <Link to="/"><p className={styles.smallLink}>← Back to Home</p></Link>
        <p className={styles.heading}>{isSignup ? "Signup" : "Login"}</p>

        {isSignup && (
          <>
            <div className="row">
              <div className="col">
                <InputControl
                  label="Full Name *"
                  placeholder="Your full name"
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, name: event.target.value }))
                  } />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <InputControl
                  label={
                    <span>
                      Email ID *
                      {isCheckingEmail && <span style={{ marginLeft: '8px', color: '#ffc107' }}>⏳</span>}
                      {!isCheckingEmail && values.email && (
                        <span style={{ marginLeft: '8px' }}>
                          {emailValidationStatus.valid ?
                            (emailValidationStatus.isEducational ?
                              <span style={{ color: '#007bff' }}>🎓</span> :
                              <span style={{ color: '#28a745' }}>✓</span>
                            ) :
                            <span style={{ color: '#dc3545' }}>✗</span>
                          }
                        </span>
                      )}
                    </span>
                  }
                  placeholder="Enter your email ID"
                  onChange={(event) => handleEmailChange(event.target.value)}
                />
                {values.role && (
                  <small className={`d-block mt-1 ${emailValidationStatus.valid ? (emailValidationStatus.isEducational ? 'text-primary' : 'text-muted') : 'text-danger'}`}>
                    {!values.email && (
                      <>
                        {values.role === 'Student' && '✓ Use recognized free email (gmail, yahoo, hotmail, outlook, etc.)'}
                        {values.role === 'Professional' && '⚠ Corporate/business email required (no free providers)'}
                        {values.role === 'Organisation' && '⚠ Educational or corporate email required (no free providers)'}
                      </>
                    )}
                    {values.email && !emailValidationStatus.valid && (
                      <span>{emailValidationStatus.message}</span>
                    )}
                    {values.email && emailValidationStatus.valid && emailValidationStatus.isEducational && (
                      <span style={{ color: '#007bff', fontWeight: '500' }}>
                        🎓 Educational email verified - Perfect for {values.role}!
                      </span>
                    )}
                    {values.email && emailValidationStatus.valid && !emailValidationStatus.isEducational && (
                      <span style={{ color: '#28a745' }}>✓ Email is valid for {values.role}</span>
                    )}
                  </small>
                )}
              </div>
              <div className="col">
                <InputControl
                  label="Phone No *"
                  placeholder="Enter your phone number"
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, phone: event.target.value }))
                  } />
              </div>
            </div>
          </>
        )}
        {!isSignup && (
          <InputControl
            label="User ID *"
            placeholder="Enter your userID or emailID"
            onChange={(event) =>
              setValues((prev) => ({ ...prev, email: event.target.value }))
            }
          />)}
        <div className="row">
          <div className="col">
            <InputControl
              label="Password *"
              placeholder="Enter your password"
              onChange={(event) =>
                setValues((prev) => ({ ...prev, password: event.target.value }))
              }
              isPassword
            />
          </div>
          {isSignup && (
            <div className="col">
              <InputControl
                label="Confirm Password *"
                placeholder="Confirm your password"
                onChange={(event) =>
                  setValues((prev) => ({ ...prev, confirmPassword: event.target.value }))
                }
                isPassword
              />
            </div>
          )}
        </div>
        {isSignup && (
          <div className="row">
            <MDBCol md='12' className='mb-3'>
              <h6 className="fw-bold mb-3">Select Your Role *</h6>
              <div className="d-flex gap-4">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="role"
                    id="roleStudent"
                    value="Student"
                    checked={values.role === 'Student'}
                    onChange={(e) => setValues((prev) => ({ ...prev, role: e.target.value }))}
                  />
                  <label className="form-check-label" htmlFor="roleStudent">
                    Student
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="role"
                    id="roleProfessional"
                    value="Professional"
                    checked={values.role === 'Professional'}
                    onChange={(e) => setValues((prev) => ({ ...prev, role: e.target.value }))}
                  />
                  <label className="form-check-label" htmlFor="roleProfessional">
                    Professional
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="role"
                    id="roleOrganisation"
                    value="Organisation"
                    checked={values.role === 'Organisation'}
                    onChange={(e) => setValues((prev) => ({ ...prev, role: e.target.value }))}
                  />
                  <label className="form-check-label" htmlFor="roleOrganisation">
                    Organisation
                  </label>
                </div>
              </div>
              <small className="text-muted">Choose your role to get appropriate access</small>
            </MDBCol>
          </div>
        )}
        <p className={styles.error}>{errorMsg}</p>

        <button type="submit" disabled={submitButtonDisabled}>
          {isSignup ? "Signup" : "Login"}
        </button>

        <div className={styles.bottom}>
          {isSignup ? (
            <p>
              Already have an account ? <Link to="/login">Login here</Link>
            </p>
          ) : (
            <p>
              New here ? <Link to="/signup">Create an account</Link>
            </p>
          )}
        </div>
      </form>
    </div>
  );
}

export default Auth;
